import React, { useEffect, useMemo, useState } from 'react';
import { Empty, Spin, Typography } from 'antd';
import api from '../../api';
import { ReasonsOutcomesTab } from '../../pages/AnalyticsPage';
import CallsDrawer from './CallsDrawer';
import WindowNote from './WindowNote';

/**
 * What the AI heard the campaign being called about, and how those calls ended.
 *
 * Business-wide rather than agent-facing: why people called and what happened
 * is a fact about demand, not about how anybody handled it. That is the whole
 * reason it sits apart from Agent Insights -- a reason count read as a
 * performance measure would be a misreading of it.
 *
 * Renders the Analytics page's own component rather than a second copy of
 * those tables, so a fix there lands here.
 */
export default function AIBusinessInsightsTab({ campaignId, campaign, from, to, skills }) {
  const skill = skills?.join(',') || undefined;   // the Skill Group's skills
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill]     = useState(null);   // { title, params }

  // No filter beyond the campaign and the window: these tables are about every
  // call, not a narrowed population.
  const cxNo = campaign?.CXCampaignNo;
  const filters = useMemo(
    () => ({ campaign: cxNo, start_date: from, end_date: to, ...(skill ? { skill } : {}) }),
    [cxNo, from, to, skill]
  );

  useEffect(() => {
    if (!cxNo || !from || !to) { setData(null); return; }
    setLoading(true);
    api.get('/api/analytics', { params: filters })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [cxNo, from, to, filters]);

  if (!campaignId) {
    return <Empty description="Select a campaign" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 40 }} />;
  }

  return (
    <>
      <WindowNote from={from} to={to} />

      <Typography.Title level={5} style={{ marginTop: 8 }}>Reasons and Outcomes</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 16 }}>
        Click a reason to see the outcomes that followed it.
      </Typography.Paragraph>

      <Spin spinning={loading}>
        <ReasonsOutcomesTab
          data={data}
          filters={filters}
          onDrillDown={(key, value) => setDrill({
            title: value,
            params: {
              campaign:   cxNo,
              start_date: from,
              end_date:   to,
              skill,
              page_size:  100,
              [key]:      value,
            },
          })}
        />
      </Spin>

      <CallsDrawer drill={drill} onClose={() => setDrill(null)} />
    </>
  );
}
